import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { getBookmakerAbbreviation } from '../utils/propConstants';

const formatOdds = (price) => {
  if (price === null || price === undefined) return '—';
  return price > 0 ? `+${price}` : `${price}`;
};

const PlayerPropCard = ({ playerName, outcomes, bookmakers, marketKey }) => {
  if (!outcomes || outcomes.length === 0) return null;

  const line = outcomes[0]?.point;

  // Find which bookmaker has the best (highest) price for each side
  const getBestBookmaker = (outcomeName) => {
    let bestKey = null;
    let bestPrice = -Infinity;
    bookmakers.forEach((bm) => {
      const o = outcomes.find((x) => x.name === outcomeName && x.bookmaker === bm.key);
      if (o && o.price > bestPrice) {
        bestPrice = o.price;
        bestKey = bm.key;
      }
    });
    return bestKey;
  };

  const bestOverBm = getBestBookmaker('Over');
  const bestUnderBm = getBestBookmaker('Under');

  // Filter to bookmakers that have at least one side
  const activeBms = bookmakers.filter((bm) =>
    outcomes.some((o) => o.bookmaker === bm.key)
  );

  if (activeBms.length === 0) return null;

  return (
    <Card sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: '12px !important', pt: 1.5, px: 2 }}>
        {/* Header: player name + line */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {playerName}
          </Typography>
          {line !== undefined && line !== null && (
            <Chip
              label={line}
              size="small"
              color="primary"
              sx={{ fontWeight: 700, fontSize: '0.85rem' }}
            />
          )}
        </Box>

        {/* Odds table */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 'max-content' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 60, pl: 0 }}>
                  O/U
                </TableCell>
                {activeBms.map((bm) => (
                  <TableCell
                    key={bm.key}
                    align="center"
                    sx={{ fontWeight: 700, fontSize: '0.7rem', px: 0.5, minWidth: 52 }}
                  >
                    {getBookmakerAbbreviation(bm.title)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {['Over', 'Under'].map((side) => {
                const bestBm = side === 'Over' ? bestOverBm : bestUnderBm;
                return (
                  <TableRow key={side} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.78rem',
                        color: 'text.secondary',
                        pl: 0,
                        py: 0.5,
                      }}
                    >
                      {side}
                    </TableCell>
                    {activeBms.map((bm) => {
                      const outcome = outcomes.find(
                        (o) => o.name === side && o.bookmaker === bm.key
                      );
                      const isBest = bm.key === bestBm && outcome;
                      return (
                        <TableCell
                          key={bm.key}
                          align="center"
                          sx={{
                            px: 0.5,
                            py: 0.5,
                            fontSize: '0.78rem',
                            fontWeight: isBest ? 700 : 400,
                            color: isBest ? 'secondary.main' : 'text.primary',
                            bgcolor: isBest ? 'rgba(74, 222, 128, 0.08)' : 'transparent',
                            borderRadius: 1,
                          }}
                        >
                          {outcome ? formatOdds(outcome.price) : '—'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default PlayerPropCard;
